/**
 * Tier Sale Progress Tracker
 * Matches the discount logic exactly - tracks tier-sale products and tier-tagged rewards
 */

class TierSaleTracker {
    constructor() {
      this.progressBar = null;
      this.isInitialized = false;
      this.maxTiers = 5;
      this.updateTierProgress = this.updateTierProgress.bind(this);
      this.isUpdatingTiers = false; // Race condition protection
      this.updateTimeout = null; // Overall update timeout
      this.stuckStateRecoveryTimeout = null; // Recovery mechanism
      
      this.init();
    }
  
    init() {
      // Wait for cart drawer to be available
      setTimeout(() => {
        this.updateTierProgress();
        this.setupEventListeners();
      }, 500);
    }


  
    isTierSaleEnabled() {
      // Check if the tier sale feature is enabled in theme settings
      // This checks if the progress bar element exists, which means the Liquid template rendered it
      // The Liquid template only renders if settings.enable_tier_sale is true
      return document.querySelector('[data-tier-sale-progress]') !== null;
    }
  
    setupEventListeners() {
      // Theme-specific cart events
      const cartEvents = [
        'theme:cart:added',
        'theme:cart:loaded', 
        'theme:cart:updated',
        'cart:updated',
        'cart:item-added',
        'cart:item-removed'
      ];
      
      cartEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          this.handleCartChange();
        });
      });
  
      // Cart drawer interactions
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-quantity-button], [data-item-remove], [data-cart-item-remove]')) {
          this.handleCartChange(500);
        }
        
        if (e.target.matches('[data-add-to-cart], button[type="submit"]')) {
          this.handleCartChange(800);
        }
      });
  
      // Quantity changes
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="updates[]"], [data-quantity-field]')) {
          this.handleCartChange(300);
        }
      });
  
      // Tier rewards dropdown events
      this.setupDropdownEventListeners();
  
      // MutationObserver for DOM changes
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const target = mutation.target;
            if (target.closest('.cart-drawer__items, .cart__items, #cartItemsHolder')) {
              shouldUpdate = true;
            }
          }
        });
        
        if (shouldUpdate) {
          this.handleCartChange(200);
        }
      });
  
      // Observe cart container changes
      const cartContainer = document.querySelector('.cart-drawer__items, .cart__items, #cartItemsHolder');
      if (cartContainer) {
        observer.observe(cartContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  
    setupDropdownEventListeners() {
      document.addEventListener('click', (e) => {
        // Show dropdown when browse rewards button is clicked
        if (e.target.matches('[data-tier-sale-browse-rewards]') || e.target.closest('[data-tier-sale-browse-rewards]')) {
          e.preventDefault();
          this.showRewardsDropdown();
        }
        
        // Hide dropdown when backdrop or close button is clicked
        if (e.target.matches('[data-tier-rewards-backdrop], [data-tier-rewards-close]') || e.target.closest('[data-tier-rewards-close]')) {
          this.hideRewardsDropdown();
        }
      });
  
      // Handle escape key to close dropdown
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
          if (dropdown) {
            this.hideRewardsDropdown();
          }
        }
      });
  
      // Note: Cart event listeners are already set up in setupEventListeners()
      // Removed duplicate listeners to prevent race conditions
  
      // Handle tier reward add to cart with enhanced loading states
      document.addEventListener('submit', (e) => {
        if (e.target.matches('[data-tier-reward-form]')) {
          e.preventDefault();
          

          
          // Prevent concurrent operations
          if (this.isUpdating) {
            console.log('⏸️ Cart operation already in progress, ignoring click');
            return;
          }
          
          const form = e.target;
          const product = form.closest('.tier-reward-product');
          const addButton = form.querySelector('[data-tier-reward-add]');
          

          
          // Block further operations
          this.isUpdating = true;
          
          // Show enhanced loading state
          this.showTierRewardLoading(product, addButton);
          
          const formData = new FormData(form);
          
          // Add to cart via fetch
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json'
            }
          })
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            console.log('✅ Tier reward added to cart:', data);
            
            // Show success state
            this.showTierRewardSuccess(product, addButton);
            
            // Trigger cart update events that the theme listens to
            document.dispatchEvent(new CustomEvent('theme:cart:added', { detail: data }));
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
            
            // Update our tier progress
            setTimeout(() => this.updateTierProgress(), 100);
            
            // Reset to added state after success animation
            setTimeout(() => {
              this.setTierRewardAdded(product, addButton);
              this.isUpdating = false; // Allow new operations
            }, 2000);
          })
          .catch(error => {
            console.error('❌ Error adding tier reward to cart:', error);
            
            // Show error and reset button
            this.showTierRewardError(product, addButton, error.message);
            setTimeout(() => {
              this.resetTierRewardState(product, addButton);
              this.isUpdating = false; // Allow new operations
            }, 3000);
          });
        }
      });
    }
  
    async handleCartChange(delay = 300) {

      
      // Race condition protection
      if (this.isUpdatingTiers) {
        console.log('⚠️ Tier update already in progress, debouncing...');
        // Force clear any stuck loading states from previous operation
        this.clearAllLoadingStates();
      }
      
      // Show immediate loading feedback for progress bar AND dropdown
      this.showProgressLoading();
      this.showDropdownCartLoading();
      
      
      // Clear any existing timeouts
      this.clearAllTimeouts();
      
      // Set up stuck state recovery (if update takes too long)
      this.stuckStateRecoveryTimeout = setTimeout(() => {
        console.error('⚠️ Tier update appears stuck, forcing recovery...');
        this.forceRecoveryFromStuckState();
      }, 15000); // 15 second timeout
      
      // Debounce the actual update with the specified delay
      this.cartChangeTimeout = setTimeout(async () => {
        try {
          // Set race condition flag
          this.isUpdatingTiers = true;
          
          // Set overall update timeout (10 seconds max)
          this.updateTimeout = setTimeout(() => {
            console.error('⚠️ Tier update timeout exceeded, forcing cleanup...');
            this.forceRecoveryFromStuckState();
          }, 10000);
          
          await this.updateTierProgress();
          
          // If modal is open, also refresh modal availability
          const isModalOpen = document.querySelector('.tier-rewards-dropdown.is-active');
          if (isModalOpen) {
            await this.refreshModalAvailability();
          }
          
          // COMPREHENSIVE CLEANUP - Clear ALL loading states
          this.clearAllLoadingStates();
          this.clearUpdateState();
          

          
        } catch (error) {
          console.error('❌ Error in cart change handler:', error);
          this.forceRecoveryFromStuckState();
        }
      }, delay);
    }
  
        async updateTierProgress() {
      try {
        this.progressBar = document.querySelector('[data-tier-sale-progress]');
        if (!this.progressBar) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }
        
        // Check if tier sale is enabled in theme settings
        if (!this.isTierSaleEnabled()) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }

        // Get current cart data
        const cartData = await this.getCartData();
        if (!cartData) {
          this.hideProgressLoading();
          this.clearUpdateState();
          return;
        }
  
        // Count tier-sale items (matching discount logic: hasTierSaleTag: true)
        const tierSaleCount = await this.countTierSaleItems(cartData);
        
        // Calculate qualified tier (same logic as discount system)
        const qualifiedTier = this.calculateQualifiedTier(tierSaleCount);
        
        // Count tier-tagged products in cart
        const tierData = await this.countTierTaggedProducts(cartData);
        const tierCounts = tierData.counts;
        const tierProductIds = tierData.productIds;
        
        // Calculate slot system data
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        
        // Calculate available rewards
        const rewardData = this.calculateAvailableRewards(qualifiedTier, tierCounts, slotData);
        
        // Update the UI
        await this.updateProgressUI(tierSaleCount, qualifiedTier, rewardData, tierProductIds);
        
        // Hide progress loading state
        this.hideProgressLoading();
        
      } catch (error) {
        console.error('❌ Error updating tier progress:', error);
        this.hideProgressLoading();
        
        // Don't clear update state here - let handleCartChange manage it
        // This allows the timeout recovery to work properly
      }
    }
  
    // Helper method for fetch with timeout
    async fetchWithTimeout(url, options = {}, timeout = 5000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
    }
  
    async getCartData() {
      try {
        const response = await this.fetchWithTimeout('/cart.json', {}, 5000);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        console.error('❌ Error fetching cart data:', error);
        return null;
      }
    }
  
    async countTierSaleItems(cartData) {
      let count = 0;
      
      for (const item of cartData.items) {
        // Check if product has tier-sale tag (corresponds to hasTierSaleTag: true in discount)
        const hasTierSaleTag = await this.checkProductHasTierSaleTag(item);
        if (hasTierSaleTag) {
          count += item.quantity;
        }
      }
      
      return count;
    }
  
    async checkProductHasTierSaleTag(cartItem) {
      try {
        // Try to get tags from cart item data first (faster)
        if (cartItem.product_tags && cartItem.product_tags.includes('tier-sale')) {
          return true;
        }
        
        // Fallback: fetch product data to get accurate tags
        const productHandle = cartItem.handle;
        if (!productHandle) return false;
        
        const response = await this.fetchWithTimeout(`/products/${productHandle}.js`, {}, 3000);
        if (!response.ok) return false;
        
        const productData = await response.json();
        const tags = productData.tags || [];
        
        return tags.includes('tier-sale');
      } catch (error) {
        console.warn('⚠️ Could not check tier-sale tag for product:', cartItem.handle, error.message);
        return false;
      }
    }
  
    calculateQualifiedTier(tierSaleCount) {
      // Exact same logic as discount system: direct mapping, capped at 5
      return Math.min(5, tierSaleCount);
    }
  
    async countTierTaggedProducts(cartData) {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const productIds = { 1: [], 2: [], 3: [], 4: [], 5: [] };
      
      for (const item of cartData.items) {
        const tierTags = await this.getProductTierTags(item);
        
        // Count quantity for each tier tag found and track product IDs
        tierTags.forEach(tier => {
          if (counts[tier] !== undefined) {
            counts[tier] += item.quantity;
            // Track which products are in cart for each tier
            if (!productIds[tier].includes(item.product_id)) {
              productIds[tier].push(item.product_id);
            }
          }
        });
      }
      
      return { counts, productIds };
    }
  
    async getProductTierTags(cartItem) {
      try {
        const tierTags = [];
        
        // Try to get tags from cart item data first
        let tags = [];
        if (cartItem.product_tags) {
          tags = cartItem.product_tags;
        } else {
          // Fallback: fetch product data
          const productHandle = cartItem.handle;
          if (!productHandle) return tierTags;
          
          const response = await this.fetchWithTimeout(`/products/${productHandle}.js`, {}, 3000);
          if (!response.ok) return tierTags;
          
          const productData = await response.json();
          tags = productData.tags || [];
        }
        
        // Check for tier tags (TIER1, TIER2, TIER3, TIER4, TIER5)
        if (tags.includes('TIER1')) tierTags.push(1);
        if (tags.includes('TIER2')) tierTags.push(2);
        if (tags.includes('TIER3')) tierTags.push(3);
        if (tags.includes('TIER4')) tierTags.push(4);
        if (tags.includes('TIER5')) tierTags.push(5);
        
        return tierTags;
      } catch (error) {
        console.warn('⚠️ Could not get tier tags for product:', cartItem.handle, error.message);
        return [];
      }
    }
  
    calculateSlotSystem(tierCounts, qualifiedTier) {
      // Exact replica of Shopify function extension slot-filling logic
      const totalSlots = qualifiedTier;
      
      // Create product quantities object like the function extension
      const productQuantities = {};
      
      // Convert tierCounts to productQuantities format
      for (let tier = 1; tier <= 5; tier++) {
        if (tierCounts[tier] > 0) {
          productQuantities[`tier_${tier}`] = {
            quantity: tierCounts[tier],
            tier: tier
          };
        }
      }
      
      let filledSlots = 0;
      
      // Simulate exact function extension slot-filling algorithm
      for (let slot = 1; slot <= qualifiedTier; slot++) {

        
        // Find products that can fill this slot (tier <= slot) and are available
        const availableProducts = Object.values(productQuantities).filter(
          product => product.quantity > 0 && product.tier <= slot
        );
        
        if (availableProducts.length > 0) {
          // Sort by tier (highest first) - same as function extension
          availableProducts.sort((a, b) => b.tier - a.tier);
          const bestProduct = availableProducts[0];
          
          // Use this product for this slot
          bestProduct.quantity--;
          filledSlots++;
          

          
          // Remove product if quantity hits 0
          if (bestProduct.quantity === 0) {
            const productKey = Object.keys(productQuantities).find(
              key => productQuantities[key] === bestProduct
            );
            delete productQuantities[productKey];
          }
        } else {

        }
      }
      
      const availableSlots = Math.max(0, totalSlots - filledSlots);
      

      
      return {
        totalSlots,
        filledSlots,
        availableSlots
      };
    }
  
    calculateAvailableRewards(qualifiedTier, tierCounts, slotData) {
      // Simple slot-based calculation
      const totalClaimed = slotData.filledSlots;
      const unclaimedRewards = slotData.availableSlots;
      const maxPossibleRewards = slotData.totalSlots;
      

      
      return {
        totalAvailable: unclaimedRewards,
        totalClaimed,
        unclaimedRewards,
        tierBreakdown: {}, // No longer needed with slot system
        maxPossibleRewards
      };
    }
  
    async updateProgressUI(tierSaleCount, qualifiedTier, rewardData, tierProductIds) {
      if (!this.progressBar) return;
  
      // Update data attributes
      this.progressBar.setAttribute('data-tier-sale-items', tierSaleCount);
      this.progressBar.setAttribute('data-qualified-tier', qualifiedTier);
  
      // Update progress percentage - bar ends exactly at achieved tier marker
      let progressPercentage = 0;
      if (qualifiedTier === 0) {
        progressPercentage = 0;
      } else if (qualifiedTier >= this.maxTiers) {
        progressPercentage = 100;
      } else {
        // Progress bar reaches exactly to the achieved tier marker
        // Markers are at: 0%, 25%, 50%, 75%, 100% for tiers 1-5
        progressPercentage = (qualifiedTier - 1) * 25;
      }
      const progressFill = this.progressBar.querySelector('.tier-sale-progress__fill');
      if (progressFill) {
        progressFill.style.width = `${progressPercentage}%`;
        progressFill.setAttribute('data-progress', progressPercentage);
      }
  
      // Update status text
      const statusElement = this.progressBar.querySelector('.tier-sale-progress__status');
      if (statusElement) {
        let statusText = '';
        if (qualifiedTier === 0) {
          statusText = 'Add tier-sale items to unlock rewards';
        } else if (qualifiedTier >= this.maxTiers) {
          if (rewardData.unclaimedRewards > 0) {
            statusText = `All tiers unlocked! ${rewardData.unclaimedRewards} reward${rewardData.unclaimedRewards !== 1 ? 's' : ''} available`;
          } else {
            statusText = 'All tiers unlocked! All rewards claimed 🎉';
          }
        } else {
          const nextTier = qualifiedTier + 1;
          const itemsNeeded = nextTier - tierSaleCount;
          statusText = `Tier ${qualifiedTier}/${this.maxTiers} unlocked • ${itemsNeeded} More Tub for Tier ${nextTier}`;
        }
        statusElement.textContent = statusText;
      }
  
      // Update tier markers
      const markers = this.progressBar.querySelectorAll('.tier-sale-progress__marker');
      markers.forEach((marker, index) => {
        const tierNum = index + 1;
        marker.className = 'tier-sale-progress__marker';
        
        if (tierNum <= qualifiedTier) {
          marker.classList.add('tier-sale-progress__marker--completed');
        } else if (tierNum === qualifiedTier + 1 && qualifiedTier < this.maxTiers) {
          marker.classList.add('tier-sale-progress__marker--current');
        }
      });
  
      // Update rewards alert
      this.updateRewardsAlert(rewardData);
      
      // Update modal subtitle if modal is open
      this.updateModalSubtitle(rewardData, qualifiedTier);
      
      // Update modal content if modal is open
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (dropdown) {
        await this.updateModalContent(rewardData, qualifiedTier);
      }
    }
  
    updateRewardsAlert(rewardData) {
      let rewardsAlert = this.progressBar.querySelector('.tier-sale-progress__rewards-alert');
      
      if (rewardData.unclaimedRewards > 0) {
        if (!rewardsAlert) {
          // Create rewards alert if it doesn't exist
          rewardsAlert = this.createRewardsAlert();
          this.progressBar.querySelector('.tier-sale-progress__content').appendChild(rewardsAlert);
        }
        
        // Update rewards count
        const countElement = rewardsAlert.querySelector('.tier-sale-progress__rewards-count');
        if (countElement) {
          countElement.textContent = rewardData.unclaimedRewards;
        }
        
        // Update rewards text
        const textElement = rewardsAlert.querySelector('.tier-sale-progress__rewards-text');
        if (textElement) {
          const pluralText = rewardData.unclaimedRewards !== 1 ? 's' : '';
          textElement.innerHTML = `
            <span class="tier-sale-progress__rewards-count">${rewardData.unclaimedRewards}</span> free reward${pluralText} available
          `;
        }
        
        rewardsAlert.style.display = 'flex';
      } else if (rewardsAlert) {
        rewardsAlert.style.display = 'none';
      }
    }
  
    createRewardsAlert() {
      const alertElement = document.createElement('div');
      alertElement.className = 'tier-sale-progress__rewards-alert';
      alertElement.innerHTML = `
        <div class="tier-sale-progress__rewards-icon">🎁</div>
        <div class="tier-sale-progress__rewards-text">
          <span class="tier-sale-progress__rewards-count">0</span> free rewards available
        </div>
        <button class="tier-sale-progress__rewards-button" data-tier-sale-browse-rewards>
          Browse Rewards
        </button>
      `;
      return alertElement;
    }
  
    showRewardsDropdown() {
      const dropdown = document.querySelector('[data-tier-rewards-dropdown]');
      if (!dropdown) return;
      
      dropdown.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      
      // Show loading state immediately
      this.showModalLoading();
      
      // Refresh modal to ensure consistent state
      setTimeout(async () => {
        await this.refreshModalAvailability();
        this.hideModalLoading();
      }, 50);
      
      // Focus management for accessibility
      const closeButton = dropdown.querySelector('[data-tier-rewards-close]');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }
    }
  
    hideRewardsDropdown() {
      const dropdown = document.querySelector('[data-tier-rewards-dropdown]');
      if (!dropdown) return;
      
      dropdown.classList.remove('is-active');
      document.body.style.overflow = '';
      
      // Return focus to browse rewards button
      const browseButton = document.querySelector('[data-tier-sale-browse-rewards]');
      if (browseButton) {
        browseButton.focus();
      }
    }
  
    updateModalSubtitle(rewardData, qualifiedTier) {
      const modalSubtitle = document.querySelector('[data-tier-rewards-subtitle]');
      if (!modalSubtitle) return; // Modal not open or doesn't exist
      
      const container = document.querySelector('[data-has-rewards]');
      
      if (rewardData.unclaimedRewards > 0) {
        const pluralText = rewardData.unclaimedRewards !== 1 ? 's' : '';
        modalSubtitle.textContent = `${rewardData.unclaimedRewards} reward${pluralText} available`;
        
        // Update data attribute and enable buttons
        if (container) {
          container.setAttribute('data-has-rewards', rewardData.unclaimedRewards);
        }
        this.enableRewardButtons();
        
      } else {
        modalSubtitle.textContent = 'Browse available rewards';
        
        // Update data attribute and disable buttons
        if (container) {
          container.setAttribute('data-has-rewards', '0');
        }
        this.disableRewardButtons();
      }
    }
  
    enableRewardButtons() {
      const addButtons = document.querySelectorAll('[data-tier-reward-add]');
      addButtons.forEach(button => {
        button.disabled = false;
        button.classList.remove('tier-reward-product__add-btn--disabled');
      });
    }
  
    disableRewardButtons() {
      const addButtons = document.querySelectorAll('[data-tier-reward-add]');
      addButtons.forEach(button => {
        button.disabled = true;
        button.classList.add('tier-reward-product__add-btn--disabled');
      });
    }
  
    async refreshModalAvailability() {
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!dropdown) return;
  
      try {
        // Add timeout protection for modal refresh
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Modal refresh timeout')), 8000);
        });
        
        // Get fresh cart data with timeout protection
        const cartDataPromise = this.getCartData();
        const cartData = await Promise.race([cartDataPromise, timeoutPromise]);
        if (!cartData) return;
  

  
        // Re-calculate everything
        const tierSaleCount = await this.countTierSaleItems(cartData);
        const qualifiedTier = this.calculateQualifiedTier(tierSaleCount);
        const tierData = await this.countTierTaggedProducts(cartData);
        const tierCounts = tierData.counts;
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        const rewardData = this.calculateAvailableRewards(qualifiedTier, tierCounts, slotData);
  

  
        // Check for mismatch with progress bar data
        const progressBar = document.querySelector('[data-tier-sale-progress]');
        if (progressBar) {
          const progressTierSaleItems = progressBar.getAttribute('data-tier-sale-items');
          const progressQualifiedTier = progressBar.getAttribute('data-qualified-tier');
          

        }
  
        // Update modal subtitle
        this.updateModalSubtitle(rewardData, qualifiedTier);
        
        // Dynamically update modal content based on rewards availability
        await this.updateModalContent(rewardData, qualifiedTier);
  
      } catch (error) {
        console.error('❌ Error refreshing modal availability:', error);
      }
    }
  
      async updateModalContent(rewardData, qualifiedTier) {
      const container = document.querySelector('.tier-rewards-dropdown__products-container');
      if (!container) return;
  

  
      // Update data attribute
      container.setAttribute('data-has-rewards', rewardData.unclaimedRewards);
  
      // Update tier product visibility and button states
      await this.updateTierProductVisibility(qualifiedTier, rewardData.unclaimedRewards);
      
      // Show/hide main containers
      const productsContainer = container.querySelector('.tier-rewards-dropdown__products');
      const noRewardsContainer = container.querySelector('.tier-rewards-dropdown__no-rewards');
      
      if (rewardData.unclaimedRewards > 0) {
        // Show products container
        if (productsContainer) productsContainer.style.display = 'grid';
        if (noRewardsContainer) noRewardsContainer.style.display = 'none';
      } else {
        // Show no rewards message
        if (productsContainer) productsContainer.style.display = 'none';
        if (noRewardsContainer) noRewardsContainer.style.display = 'block';
      }
    }
  
    async updateTierProductVisibility(qualifiedTier, unclaimedRewards) {
      const allTierProducts = document.querySelectorAll('.tier-reward-product');
      

      
      // If no tiers qualified, hide all products
      if (qualifiedTier === 0) {
        allTierProducts.forEach(product => {
          product.classList.add('tier-product--hidden');
          product.setAttribute('data-tier-qualified', 0);
          product.setAttribute('data-rewards-available', 0);
        });
        console.log('❌ No tiers qualified - hiding all products');
        return;
      }
      
      // Get eligible tiers that can fill remaining slots (matching discount logic)
      const eligibleTiers = await this.calculateEligibleTiers(qualifiedTier);
      
      
      
      allTierProducts.forEach(product => {
        const productTier = parseInt(product.dataset.tier);
        const addButton = product.querySelector('[data-tier-reward-add]');
        
        // Update data attributes
        product.setAttribute('data-tier-qualified', qualifiedTier);
        product.setAttribute('data-rewards-available', unclaimedRewards);
        
        // Show/hide product based on qualified tier AND eligible tiers
        const isTierQualified = productTier <= qualifiedTier;
        const isTierEligible = eligibleTiers.includes(productTier);
        
        if (isTierQualified && (unclaimedRewards === 0 || isTierEligible)) {
          product.classList.remove('tier-product--hidden');

          
          // Enable/disable button based on available rewards and eligibility
          if (unclaimedRewards > 0 && isTierEligible) {
            product.classList.remove('tier-product--disabled');
            if (addButton) {
              addButton.disabled = false;
              addButton.classList.remove('tier-reward-product__add-btn--disabled');
              const addText = addButton.querySelector('.tier-reward-product__add-text');
              if (addText) addText.textContent = 'Add';
            }
          } else {
            product.classList.add('tier-product--disabled');
            if (addButton) {
              addButton.disabled = true;
              addButton.classList.add('tier-reward-product__add-btn--disabled');
              const addText = addButton.querySelector('.tier-reward-product__add-text');
              if (addText) {
                if (unclaimedRewards === 0) {
                  addText.textContent = 'All Slots Filled';
                } else {
                  addText.textContent = 'Slot Unavailable';
                }
              }
            }
          }
        } else {
          product.classList.add('tier-product--hidden');

        }
      });
    }
  
    async calculateEligibleTiers(qualifiedTier) {
      // Determine which tier products can fill remaining slots
      try {
        const cartData = await this.getCartData();
        if (!cartData) return [];
  
        // Get current tier counts
        const tierData = await this.countTierTaggedProducts(cartData);
        const tierCounts = tierData.counts;
        
        // Use the same slot calculation logic
        const slotData = this.calculateSlotSystem(tierCounts, qualifiedTier);
        
        // If no slots available, no tiers are eligible
        if (slotData.availableSlots === 0) {

          return [];
        }
        
        // Calculate which slots are currently empty using the same algorithm
        const productQuantities = {};
        
        // Convert tierCounts to productQuantities format
        for (let tier = 1; tier <= 5; tier++) {
          if (tierCounts[tier] > 0) {
            productQuantities[`tier_${tier}`] = {
              quantity: tierCounts[tier],
              tier: tier
            };
          }
        }
        
        const eligibleTiers = new Set();
        
        // Simulate slot filling to find empty slots
        for (let slot = 1; slot <= qualifiedTier; slot++) {
          const availableProducts = Object.values(productQuantities).filter(
            product => product.quantity > 0 && product.tier <= slot
          );
          
          if (availableProducts.length > 0) {
            // Slot can be filled - use the best product
            availableProducts.sort((a, b) => b.tier - a.tier);
            const bestProduct = availableProducts[0];
            bestProduct.quantity--;
            
            if (bestProduct.quantity === 0) {
              const productKey = Object.keys(productQuantities).find(
                key => productQuantities[key] === bestProduct
              );
              delete productQuantities[productKey];
            }
          } else {
            // Empty slot - any tier <= slot can fill it

            for (let tier = 1; tier <= slot; tier++) {
              eligibleTiers.add(tier);
            }
          }
        }
  
        const result = Array.from(eligibleTiers).sort((a, b) => a - b);

        return result;
        
      } catch (error) {
        console.error('❌ Error calculating eligible tiers:', error);
        // Fallback: show all qualified tiers if there are available slots
        return Array.from({length: qualifiedTier}, (_, i) => i + 1);
      }
    }
  
    async updateProductButtonStates(rewardData) {
      // Get all tier reward product forms in the modal
      const tierRewardForms = document.querySelectorAll('[data-tier-reward-form]');
      
      tierRewardForms.forEach(form => {
        const addButton = form.querySelector('[data-tier-reward-add]');
        if (!addButton) return;
        
        // Simple logic: Can add if there are available slots
        const canAddMore = rewardData.unclaimedRewards > 0;
        
        if (canAddMore) {
          // Enable button - there are available slots
          addButton.disabled = false;
          addButton.classList.remove('tier-reward-product__add-btn--added');
          
          const addText = addButton.querySelector('.tier-reward-product__add-text');
          if (addText) {
            addText.textContent = 'Add';
          }
          

        } else {
          // Disable button - no slots available
          addButton.disabled = true;
          addButton.classList.add('tier-reward-product__add-btn--added');
          
          const addText = addButton.querySelector('.tier-reward-product__add-text');
          if (addText) {
              addText.textContent = 'All Slots Filled';
          }
          

        }
      });
    }
  
    // Enhanced loading state methods for tier rewards
    showTierRewardLoading(product, button) {
      if (!button || !product) {
        console.error('❌ showTierRewardLoading: missing elements');
        return;
      }
      
      // Button loading state
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      
      button.classList.add('tier-reward-product__add-btn--loading');
      button.disabled = true;
      
      if (buttonText) buttonText.style.opacity = '0';
      if (buttonLoading) buttonLoading.style.opacity = '1';
      
      // Individual item status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'flex';
        statusIndicator.classList.remove('is-success');
        statusIndicator.classList.add('is-loading');
      }
      

    }
    
    showTierRewardSuccess(product, button) {
      if (!button || !product) return;
      
      // Button success state
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      button.classList.remove('tier-reward-product__add-btn--loading');
      button.classList.add('tier-reward-product__add-btn--success');
      
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '1';
      
      // Individual item status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.classList.remove('is-loading');
        statusIndicator.classList.add('is-success');
      }
      

    }
    
    setTierRewardAdded(product, button) {
      if (!button || !product) return;
      
      // Reset button classes
      button.classList.remove('tier-reward-product__add-btn--loading', 'tier-reward-product__add-btn--success');
      button.classList.add('tier-reward-product__add-btn--added');
      button.disabled = true;
      
      // Reset button elements
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Added';
      }
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '0';
      
      // Hide status indicator after delay
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        setTimeout(() => {
          statusIndicator.style.display = 'none';
          statusIndicator.classList.remove('is-loading', 'is-success');
        }, 1000);
      }
      

    }
    
    showTierRewardError(product, button, errorMessage) {
      if (!button || !product) return;
      
      // Button error state
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      
      button.classList.remove('tier-reward-product__add-btn--loading');
      button.classList.add('tier-reward-product__add-btn--error');
      
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Error';
      }
      
      // Hide status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'none';
        statusIndicator.classList.remove('is-loading', 'is-success');
      }
      

    }
    
    resetTierRewardState(product, button) {
      if (!button || !product) return;
      
      // Reset all button states
      button.classList.remove(
        'tier-reward-product__add-btn--loading', 
        'tier-reward-product__add-btn--success', 
        'tier-reward-product__add-btn--error',
        'tier-reward-product__add-btn--added'
      );
      button.disabled = false;
      
      // Reset button elements
      const buttonText = button.querySelector('.tier-reward-product__add-text');
      const buttonLoading = button.querySelector('.tier-reward-product__add-loading');
      const buttonSuccess = button.querySelector('.tier-reward-product__add-success');
      
      if (buttonText) {
        buttonText.style.opacity = '1';
        buttonText.textContent = 'Add';
      }
      if (buttonLoading) buttonLoading.style.opacity = '0';
      if (buttonSuccess) buttonSuccess.style.opacity = '0';
      
      // Hide status indicator
      const statusIndicator = product.querySelector('[data-product-status]');
      if (statusIndicator) {
        statusIndicator.style.display = 'none';
        statusIndicator.classList.remove('is-loading', 'is-success');
      }
      

    }
  
    // Legacy methods for backward compatibility
    setButtonLoadingState(button, isLoading) {
      if (!button) {
        console.error('❌ setButtonLoadingState: button is null');
        return;
      }
      
      const addText = button.querySelector('.tier-reward-product__add-text');
      
      if (isLoading) {
        button.disabled = true;
        if (addText) addText.textContent = 'Adding...';
      } else {
        button.disabled = false;
        if (addText) addText.textContent = 'Add';
      }
    }
  
    setButtonSuccessState(button) {
      if (!button) return;
      const addText = button.querySelector('.tier-reward-product__add-text');
      if (addText) addText.textContent = 'Added ✓';
    }
  
    setButtonErrorState(button, errorMessage) {
      if (!button) return;
      const addText = button.querySelector('.tier-reward-product__add-text');
      if (addText) addText.textContent = 'Error';
    }
  
    // Modal Loading States
    showModalLoading() {
      const modal = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!modal) return;
  
      // Add loading overlay to modal
      const loadingOverlay = this.createLoadingOverlay('Calculating available rewards...');
      loadingOverlay.classList.add('tier-modal-loading');
      modal.appendChild(loadingOverlay);
  

    }
  
    hideModalLoading() {
      const loadingOverlay = document.querySelector('.tier-modal-loading');
      if (loadingOverlay) {
        loadingOverlay.remove();

      }
    }
  
        // Progress Bar Loading States  
    showProgressLoading() {
      const progressBar = this.progressBar || document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.add('tier-progress-loading');
      }
    }

    hideProgressLoading() {
      // Always try to find and clear progress bar loading
      const progressBar = this.progressBar || document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.remove('tier-progress-loading');
      }
      
      // Safety net: clear any other progress bars that might be stuck
      const allProgressBars = document.querySelectorAll('[data-tier-sale-progress].tier-progress-loading');
      allProgressBars.forEach(bar => {
        bar.classList.remove('tier-progress-loading');
      });
    }
  
    // Dropdown Cart Loading States - Show when ANY cart change happens
    showDropdownCartLoading() {
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (!dropdown) return; // Only show if dropdown is open
  
      // Show the progress bar in the dropdown header
      const progressContainer = dropdown.querySelector('[data-tier-progress-container]');
      if (progressContainer) {
        progressContainer.style.display = 'block';
        progressContainer.classList.add('is-active');
        
        // Update progress bar to show loading state
        const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
        const progressText = progressContainer.querySelector('[data-tier-progress-text]');
        
        if (progressFill) {
          progressFill.style.width = '100%';
          progressFill.classList.add('tier-progress-loading');
        }
        
        if (progressText) {
          progressText.textContent = 'Updating rewards...';
        }
      }
  
      // Add shimmer effect to all products AND disable all buttons
      const products = dropdown.querySelectorAll('.tier-reward-product');
      products.forEach(product => {
        product.classList.add('tier-progress-loading');
        
        // Disable all add buttons during loading
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.disabled = true;
          addButton.classList.add('tier-reward-product__add-btn--loading-disabled');
        }
      });
  

    }
  
        hideDropdownCartLoading() {
      // Clear loading from dropdown if it exists
      const dropdown = document.querySelector('.tier-rewards-dropdown.is-active');
      if (dropdown) {
        // Hide the progress bar in the dropdown header
        const progressContainer = dropdown.querySelector('[data-tier-progress-container]');
        if (progressContainer) {
          // Immediate hide without animation to prevent stuck states
          progressContainer.classList.remove('is-active');
          progressContainer.style.display = 'none';
          
          // Reset progress bar
          const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
          if (progressFill) {
            progressFill.style.width = '0%';
            progressFill.classList.remove('tier-progress-loading');
          }
        }

        // Remove shimmer effect from dropdown products
        const products = dropdown.querySelectorAll('.tier-reward-product');
        products.forEach(product => {
          product.classList.remove('tier-progress-loading');
          
          const addButton = product.querySelector('[data-tier-reward-add]');
          if (addButton) {
            addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
          }
        });
      }
      
      // ALSO clear loading from ANY products outside dropdown (safety net)
      const allProducts = document.querySelectorAll('.tier-reward-product.tier-progress-loading');
      allProducts.forEach(product => {
        product.classList.remove('tier-progress-loading');
        
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
        }
      });
    }
  
  
  
    // Helper Methods for State Management
    clearUpdateState() {
      this.isUpdatingTiers = false;
      
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }
      

    }

    clearAllTimeouts() {
      if (this.cartChangeTimeout) {
        clearTimeout(this.cartChangeTimeout);
        this.cartChangeTimeout = null;
      }
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }

    }

    clearAllLoadingStates() {

      
      // Clear progress bar loading
      this.hideProgressLoading();
      
      // Clear dropdown cart loading
      this.hideDropdownCartLoading();
      
      // Clear modal loading
      this.hideModalLoading();
      
      // Force remove any stuck classes from progress bar
      const progressBar = document.querySelector('[data-tier-sale-progress]');
      if (progressBar) {
        progressBar.classList.remove('tier-progress-loading');
      }
      
      // Force remove loading classes from all products
      const products = document.querySelectorAll('.tier-reward-product');
      products.forEach(product => {
        product.classList.remove('tier-progress-loading');
        
        const addButton = product.querySelector('[data-tier-reward-add]');
        if (addButton) {
          addButton.classList.remove('tier-reward-product__add-btn--loading-disabled');
        }
      });
      
      // Clear any progress containers
      const progressContainer = document.querySelector('[data-tier-progress-container]');
      if (progressContainer) {
        progressContainer.style.display = 'none';
        progressContainer.classList.remove('is-active');
        
        const progressFill = progressContainer.querySelector('[data-tier-progress-fill]');
        if (progressFill) {
          progressFill.style.width = '0%';
          progressFill.classList.remove('tier-progress-loading');
        }
      }
      
      // Remove any loading overlays
      const loadingOverlays = document.querySelectorAll('.tier-loading-overlay, .tier-modal-loading');
      loadingOverlays.forEach(overlay => overlay.remove());
      

    }
  
    forceRecoveryFromStuckState() {
      console.log('🚨 Forcing recovery from stuck state...');
      
      // Clear all timeouts first
      this.clearAllTimeouts();
      
      // Clear all state
      this.clearUpdateState();
      
      // Use comprehensive loading state cleanup
      this.clearAllLoadingStates();
      
      console.log('✅ Recovery completed - system should be responsive again');
      
      // Try a gentle retry after a delay
      setTimeout(() => {
        if (!this.isUpdatingTiers) {
          console.log('🔄 Attempting gentle retry after recovery...');
          this.updateTierProgress().catch(error => {
            console.log('⚠️ Gentle retry failed, but system is still recovered:', error.message);
          });
        }
      }, 2000);
    }
  
    createLoadingOverlay(message = 'Loading...') {
      const overlay = document.createElement('div');
      overlay.className = 'tier-loading-overlay';
      overlay.innerHTML = `
        <div class="tier-loading-content">
          <div class="tier-loading-spinner">
            <svg width="32" height="32" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="75.4" stroke-dashoffset="75.4" opacity="0.4"/>
              <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="75.4" stroke-dashoffset="56.5"/>
            </svg>
          </div>
          <p class="tier-loading-text">${message}</p>
        </div>
      `;
      return overlay;
    }
  
  
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.tierSaleTracker = new TierSaleTracker();
    });
  } else {
    window.tierSaleTracker = new TierSaleTracker();
  }
  
  // Also initialize on page navigation (for SPA-like themes)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      if (window.tierSaleTracker) {
        window.tierSaleTracker.forceRecoveryFromStuckState();
      }
      window.tierSaleTracker = new TierSaleTracker();
    }, 100);
  });
  
  // Add manual test and recovery functions for debugging
  window.testTierSaleTracker = function() {
    console.log('=== Testing TierSaleTracker ===');
    console.log('Tracker exists:', !!window.tierSaleTracker);
    console.log('Is initialized:', window.tierSaleTracker?.isInitialized);
    console.log('Progress bar found:', !!window.tierSaleTracker?.progressBar);
    console.log('Is updating:', window.tierSaleTracker?.isUpdatingTiers);
    
    if (window.tierSaleTracker) {
      window.tierSaleTracker.updateTierProgress();
    }
  };
  
  // Manual recovery function for stuck states
  window.recoverTierSaleTracker = function() {
    console.log('=== Manual Recovery Triggered ===');
    if (window.tierSaleTracker) {
      window.tierSaleTracker.forceRecoveryFromStuckState();
    } else {
      console.log('❌ TierSaleTracker not found');
    }
  };

  // Manual function to clear just loading states
  window.clearTierSaleLoading = function() {
    console.log('=== Manual Loading State Clear ===');
    if (window.tierSaleTracker) {
      window.tierSaleTracker.clearAllLoadingStates();
    } else {
      console.log('❌ TierSaleTracker not found');
    }
  }; 